import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import {
  APP_VERSION,
  DEBUG,
  EXPORT_NOTIFICATION,
  FEATURE_CLIENT_ID,
  GLOBAL_SERVICES_KEY,
  GLOBAL_SERVICES_URL,
  N8N_BASE_URL,
  N8N_ONBOARDING_TRIAL_IMPORT_ID,
  N8N_TRIAL_USERS_CONVERT_ID,
  OAUTH2_CLIENT_ID,
  OAUTH2_CLIENT_ID_EMEA,
  PRODUCT,
  SMART_FILL,
  WEBHOOK_ID,
} from './injection-token';
import { TokenInterceptor } from './interceptor/token.interceptor';
import { N8NConfigModel, OAuth2EnvModel } from './models';
import { Product } from './models/product.model';
import { OpenAiService } from './services';
import { ActionPlanTaskService } from './services/action-plan-task.service';
import { AddressBookService } from './services/address-book.service';
import { AddressService } from './services/address.service';
import { AppointmentService } from './services/appointment.service';
import { AvatarService } from './services/avatar.service';
import { ColumnSetupService } from './services/column-setup.service';
import { ConfigurationService } from './services/configuration.service';
import { ContextService } from './services/context.service';
import { CustomActionService } from './services/custom-actions.service';
import { CustomizationStringService } from './services/customization-string.service';
import { DocumentService } from './services/document.service';
import { DuplicateCheckService } from './services/duplicate-check.service';
import { EmailDocumentService } from './services/email-document.service';
import { EmailProfileService } from './services/email-profile.service';
import { EmailValidationService } from './services/email-validation.service';
import { FavoriteListService } from './services/favorite-list.service';
import {
  FeatureService,
  OnPremiseFeatureService,
} from './services/feature.service';
import { FieldOptionsService } from './services/field-options.service';
import { FollowingTabService } from './services/following-tab.service';
import { GooglePlaceService } from './services/google-places.service';
import { InteractionLogService } from './services/interaction-log.service';
import { LocaleService } from './services/locale.service';
import { MetadataService } from './services/metadata.service';
import { NoteService } from './services/note.service';
import { NotificationSetupService } from './services/notification-setup.service';
import { OpportunityService } from './services/opportunity.service';
import { RecentEntryService } from './services/recent-entry.service';
import { RelatedEntryService } from './services/related-entry.service';
import { SalesTeamService } from './services/sales-team.service';
import { SearchCatalogService } from './services/search-catalog.service';
import { SecurityGroupService } from './services/security-group.service';
import { SessionStorageService } from './services/session-storage.service';
import { SessionService } from './services/session.service';
import { SmartFillService } from './services/smart-fill.service';
import { StatisticsService } from './services/statistics.service';
import { SystemService } from './services/system.service';
import { TabsService } from './services/tabs.service';
import { TaskService } from './services/task.service';
import { TokenService } from './services/token.service';
import { TrackEventService } from './services/track-event.service';
import { UdoService } from './services/udo.service';
import { UserService } from './services/user.service';
import { WidgetLayoutService } from './services/widget-layout.service';
import { WorkflowActionService } from './services/workflow-action.service';
import { WorkflowInstanceService } from './services/workflow-instance.service';
import { WorkflowStageTemplateService } from './services/workflow-stage-template.service';
import { WorkflowTaskTemplateService } from './services/workflow-task-template.service';
import { WorkflowService } from './services/workflow.service';
import { GlobalStore } from './state/global.store';
import { OpportunityStore } from './state/opportunity.store';

export interface SharedDataAccessConfig {
  debug: boolean;
  featureClientId?: string;
  smartFill?: string;
  exportRowsNotification?: number;
  webhookRecipientId?: string;
  appVersion?: string;
  product?: string;
  oauth2?: OAuth2EnvModel;
  n8nConfig?: N8NConfigModel;
}

export function provideSharedDataAccess(
  config: SharedDataAccessConfig,
): EnvironmentProviders {
  return makeEnvironmentProviders([
    ActionPlanTaskService,
    AddressBookService,
    AddressService,
    AppointmentService,
    AvatarService,
    ColumnSetupService,
    ConfigurationService,
    ContextService,
    CustomActionService,
    CustomizationStringService,
    DocumentService,
    DuplicateCheckService,
    EmailDocumentService,
    EmailProfileService,
    EmailValidationService,
    {
      provide: FeatureService,
      useFactory: (context: ContextService) => {
        if (context.isCloud) {
          return new FeatureService(
            context,
            config.featureClientId ?? '',
            config.debug,
          );
        } else {
          return new OnPremiseFeatureService(
            context,
            config.featureClientId ?? '',
            config.debug,
          );
        }
      },
      deps: [ContextService],
    },
    FavoriteListService,
    FieldOptionsService,
    FollowingTabService,
    GlobalStore,
    GooglePlaceService,
    InteractionLogService,
    LocaleService,
    MetadataService,
    NoteService,
    NotificationSetupService,
    OpenAiService,
    OpportunityService,
    OpportunityStore,
    RecentEntryService,
    RelatedEntryService,
    SalesTeamService,
    SearchCatalogService,
    SecurityGroupService,
    SessionStorageService,
    SessionService,
    SmartFillService,
    StatisticsService,
    SystemService,
    TabsService,
    TaskService,
    TokenService,
    TrackEventService,
    UdoService,
    UserService,
    WidgetLayoutService,
    WorkflowService,
    WorkflowActionService,
    WorkflowTaskTemplateService,
    WorkflowInstanceService,
    WorkflowStageTemplateService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    { provide: DEBUG, useValue: config.debug },
    { provide: FEATURE_CLIENT_ID, useValue: config.featureClientId },
    { provide: SMART_FILL, useValue: config.smartFill },
    {
      provide: EXPORT_NOTIFICATION,
      useValue: config.exportRowsNotification,
    },
    { provide: WEBHOOK_ID, useValue: config.webhookRecipientId },
    { provide: APP_VERSION, useValue: config.appVersion },
    { provide: PRODUCT, useValue: config.product ?? Product.Core },
    {
      provide: GLOBAL_SERVICES_URL,
      useValue: config.oauth2?.globalServicesUrl,
    },
    {
      provide: GLOBAL_SERVICES_KEY,
      useValue: config.oauth2?.globalServicesKey,
    },
    { provide: OAUTH2_CLIENT_ID, useValue: config.oauth2?.oauth2ClientId },
    {
      provide: OAUTH2_CLIENT_ID_EMEA,
      useValue: config.oauth2?.oauth2ClientIdEmea,
    },
    { provide: N8N_BASE_URL, useValue: config.n8nConfig?.baseUrl },
    {
      provide: N8N_ONBOARDING_TRIAL_IMPORT_ID,
      useValue: config.n8nConfig?.trialImportDataId,
    },
    {
      provide: N8N_TRIAL_USERS_CONVERT_ID,
      useValue: config.n8nConfig?.convertTrialUserId,
    },
  ]);
}
